// src/components/DocumentUpload.jsx
// Reusable document upload component for model cards and business context documents
// Upload only - processing happens when assessment starts

import React, { useState, useRef, useCallback } from 'react';
import {
  Stack,
  Text,
  Group,
  Button,
  Card,
  Progress,
  Badge,
  ActionIcon,
  Tooltip,
  List,
  ThemeIcon
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileText,
  IconTrash,
  IconCheck,
  IconAlertCircle,
  IconCloud
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { uploadViaSupabase } from '../utils/supabaseUpload';
import { useAuth0 } from '@auth0/auth0-react';
import { authFetch } from '../utils/authFetch';

// File type icon mapping
const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return IconFileTypePdf;
    case 'doc':
    case 'docx':
      return IconFileTypeDoc;
    case 'txt':
    case 'md':
      return IconFileText;
    default:
      return IconFile;
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function DocumentUpload({
  modelId,
  documentType = 'model_card',
  label = 'Model Documentation',
  description = 'Upload model card or system documentation',
  optional = true,
  acceptedTypes = '.pdf,.docx,.doc,.txt,.md',
  maxSize = 10 * 1024 * 1024, // 10MB
  bucket = 'model-cards',
  existingDocuments = [],
  onUploadComplete,
  onDocumentDeleted,
  showImportButton = false, // Mock import from cloud button
  compact = false
}) {
  const { user } = useAuth0();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input
    event.target.value = '';

    // Validate file size
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is ${formatFileSize(maxSize)}`);
      notifications.show({
        title: 'File too large',
        message: `Maximum file size is ${formatFileSize(maxSize)}`,
        color: 'red'
      });
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(10);

    try {
      // Step 1: Upload to Supabase Storage
      setUploadProgress(20);

      const path = `${modelId}/${Date.now()}-${file.name}`;
      const uploadResult = await uploadViaSupabase({
        file,
        bucket,
        path,
        contentType: file.type || 'application/octet-stream'
      });

      setUploadProgress(60);

      // Step 2: Save metadata to assessment_documents table
      const docResponse = await authFetch('/.netlify/functions/manageAssessmentDocuments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'create',
          modelUuid: modelId,
          documentType,
          fileName: file.name,
          fileUrl: `${process.env.REACT_APP_SUPABASE_DATABASE_URL}/storage/v1/object/public/${bucket}/${uploadResult.path}`,
          filePath: uploadResult.path,
          bucket,
          fileSize: file.size,
          contentType: file.type || 'application/octet-stream',
          uploadedBy: user?.sub || null
        })
      });

      if (!docResponse.ok) {
        const errData = await docResponse.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save document metadata');
      }

      const { document } = await docResponse.json();

      setUploadProgress(100);

      notifications.show({
        title: 'Document uploaded',
        message: `${file.name} uploaded successfully`,
        color: 'green'
      });

      // Callback with document info
      onUploadComplete?.({
        id: document.id,
        fileName: file.name,
        fileUrl: document.file_url,
        filePath: document.file_path,
        fileSize: file.size,
        contentType: file.type,
        documentType,
        bucket,
        uploadedAt: document.uploaded_at
      });

    } catch (err) {
      console.error('Document upload failed:', err);
      setError(err.message);
      notifications.show({
        title: 'Upload failed',
        message: err.message,
        color: 'red'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [modelId, documentType, bucket, maxSize, user, onUploadComplete]);

  const handleDelete = useCallback(async (documentId) => {
    try {
      const response = await authFetch('/.netlify/functions/manageAssessmentDocuments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'delete',
          documentId
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to delete document');
      }

      notifications.show({
        title: 'Document deleted',
        message: 'Document removed successfully',
        color: 'blue'
      });

      onDocumentDeleted?.(documentId);
    } catch (err) {
      console.error('Delete failed:', err);
      notifications.show({
        title: 'Delete failed',
        message: err.message,
        color: 'red'
      });
    }
  }, [onDocumentDeleted]);

  const handleImportFromCloud = useCallback(() => {
    notifications.show({
      title: 'Coming Soon',
      message: 'Import from SageMaker, Bedrock, and HuggingFace will be available soon',
      color: 'blue'
    });
  }, []);

  // Compact mode for inline use
  if (compact) {
    return (
      <Stack gap="xs">
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {existingDocuments.length > 0 ? (
          <Group gap="xs" wrap="wrap">
            {existingDocuments.map((doc) => {
              const FileIcon = getFileIcon(doc.fileName || doc.file_name);
              return (
                <Badge
                  key={doc.id}
                  variant="light"
                  color="green"
                  leftSection={<FileIcon size={14} />}
                  rightSection={
                    <ActionIcon
                      size="xs"
                      variant="transparent"
                      color="red"
                      onClick={() => handleDelete(doc.id)}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  }
                >
                  {(doc.fileName || doc.file_name)?.substring(0, 20)}
                  {(doc.fileName || doc.file_name)?.length > 20 ? '...' : ''}
                </Badge>
              );
            })}
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconUpload size={14} />}
              onClick={() => fileInputRef.current?.click()}
              loading={uploading}
            >
              Add more
            </Button>
          </Group>
        ) : (
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconUpload size={14} />}
            onClick={() => fileInputRef.current?.click()}
            loading={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload document'}
          </Button>
        )}

        {uploading && (
          <Progress value={uploadProgress} size="xs" color="blue" animated />
        )}
      </Stack>
    );
  }

  // Full card mode
  return (
    <Card withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Group gap="xs">
              <Text fw={600} size="sm">{label}</Text>
              {optional && (
                <Badge size="xs" variant="light" color="gray">Optional</Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">{description}</Text>
          </div>
        </Group>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Existing documents */}
        {existingDocuments.length > 0 && (
          <List spacing="xs" size="sm">
            {existingDocuments.map((doc) => {
              const FileIcon = getFileIcon(doc.fileName || doc.file_name);
              return (
                <List.Item
                  key={doc.id}
                  icon={
                    <ThemeIcon color="green" size="sm" radius="xl">
                      <IconCheck size={14} />
                    </ThemeIcon>
                  }
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <FileIcon size={16} />
                      <Text size="sm" truncate style={{ maxWidth: 200 }}>
                        {doc.fileName || doc.file_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatFileSize(doc.fileSize || doc.file_size)}
                      </Text>
                    </Group>
                    <Tooltip label="Remove document">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => handleDelete(doc.id)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </List.Item>
              );
            })}
          </List>
        )}

        {/* Upload progress */}
        {uploading && (
          <Stack gap="xs">
            <Progress value={uploadProgress} size="sm" color="blue" animated />
            <Text size="xs" c="dimmed" ta="center">Uploading document...</Text>
          </Stack>
        )}

        {/* Error message */}
        {error && (
          <Group gap="xs">
            <IconAlertCircle size={16} color="red" />
            <Text size="xs" c="red">{error}</Text>
          </Group>
        )}

        {/* Upload buttons */}
        <Group gap="sm">
          <Button
            variant="light"
            leftSection={<IconUpload size={16} />}
            onClick={() => fileInputRef.current?.click()}
            loading={uploading}
            disabled={uploading}
          >
            {existingDocuments.length > 0 ? 'Upload Another' : 'Upload Document'}
          </Button>

          {showImportButton && (
            <Tooltip label="Import model documentation from cloud providers">
              <Button
                variant="subtle"
                leftSection={<IconCloud size={16} />}
                onClick={handleImportFromCloud}
                disabled
              >
                <Group gap="xs">
                  <Text size="sm">Import from Cloud</Text>
                  <Badge size="xs" variant="light" color="gray">Soon</Badge>
                </Group>
              </Button>
            </Tooltip>
          )}
        </Group>

        {/* Accepted file types */}
        <Text size="xs" c="dimmed">
          Accepted: PDF, Word, Text, Markdown. Max size: {formatFileSize(maxSize)}
        </Text>
      </Stack>
    </Card>
  );
}
